import type { Account, RestoreAccount } from '@repo/api-gw-sdk';

import type { FieldValidation } from '@/components/form/validation/fieldValidation';
import { getAccountRoleArn } from '@/utils/accounts';

/** Matches API gateway validation in create_backup_vault_impl.go */
const MRAP_ALIAS_PATTERN = /^[a-z0-9]+\.mrap$/;

export const mrapAliasFieldValidation: FieldValidation = {
  validateInput: (value: string) => {
    const trimmed = value.trim();
    return trimmed === '' || MRAP_ALIAS_PATTERN.test(trimmed);
  },
  errorMessage: () =>
    'Use the auto-generated alias ending in .mrap (e.g. mfzwi23gnjvgw.mrap)',
};

export const getByovPolicyJson = (
  bucketName: string,
  scanningAccountId: string,
  dlsgFrontend: boolean,
  restoreAccounts?: RestoreAccount[]
) => {
  const scanningAccountRoles = dlsgFrontend
    ? `[
    "arn:aws:iam::${scanningAccountId}:role/EonScanningNodeRole",
    "arn:aws:iam::${scanningAccountId}:role/S3ProxyServiceRole",
    "arn:aws:iam::${scanningAccountId}:role/eon-gw-data-access-role"
  ]`
    : `"arn:aws:iam::${scanningAccountId}:role/EonScanningNodeRole"`;
  return `
  {
    "Version": "2012-10-17",
    "Statement": [
      {
          "Effect": "Allow",
          "Principal": {
              "AWS": ${scanningAccountRoles}
          },
          "Action": [
              "s3:GetObject",
              "s3:PutObject",
              "s3:DeleteObject",
              "s3:ListBucket"
          ],
          "Resource": [
              "arn:aws:s3:::${bucketName}",
              "arn:aws:s3:::${bucketName}/*"
          ]
      }${restoreAccounts?.length ? getRestoreAccountsCode(restoreAccounts, bucketName) : ''}
    ]
}`;
};

const getRestoreAccountsCode = (
  restoreAccounts: RestoreAccount[],
  bucketName: string
) => {
  const nodeRolePrincipals = [
    ...new Set(
      restoreAccounts.map(
        (account) =>
          `arn:aws:iam::${account.providerAccountId}:role/EonRestoreNodeRole`
      )
    ),
  ];
  return `,
      {
          "Sid": "AllowRestoreAccess",
          "Effect": "Allow",
          "Principal": {
                  "AWS": [${nodeRolePrincipals.map((arn) => `"${arn}"`).join(',')}]
          },
          "Action": [
              "s3:GetObject",
              "s3:ListBucket"
          ],
          "Resource": [
              "arn:aws:s3:::${bucketName}",
              "arn:aws:s3:::${bucketName}/*"
          ]
      },
      {
          "Sid": "AthenaGetSchemaJson",
          "Effect": "Allow",
          "Principal": {
              "AWS": [${restoreAccounts.map((account) => `"${getAccountRoleArn(account)}"`).join(',')}]
          },
          "Action": "s3:GetObject",
          "Resource": [
              "arn:aws:s3:::${bucketName}/dynamodb/*/eon-*/*/*/_schema.json",
              "arn:aws:s3:::${bucketName}/db-export/*/eon-*/*/*/_schema.json",
              "arn:aws:s3:::${bucketName}/*/db-export/*/eon-*/*/*/_schema.json"
          ]
      },
      {
          "Sid": "AthenaQueryPolicy",
          "Effect": "Allow",
          "Principal": {
              "AWS": [${restoreAccounts.map((account) => `"${getAccountRoleArn(account)}"`).join(',')}]
          },
          "Action": [
              "s3:GetObject",
              "s3:ListBucket"
          ],
          "Resource": [
              "arn:aws:s3:::${bucketName}",
              "arn:aws:s3:::${bucketName}/*"
          ],
          "Condition": {
              "ForAllValues:StringEquals": {
                  "aws:CalledVia": "athena.amazonaws.com"
              }
          }
      }`;
};

export const getMrapPolicyJson = (
  mrapAlias: string,
  vaultAccount: Account | undefined,
  restoreAccounts: RestoreAccount[]
) => {
  const vaultAccountId =
    vaultAccount?.providerAccountId ?? '<vault-account-id>';
  const mrapArn = `arn:aws:s3::${vaultAccountId}:accesspoint/${mrapAlias}`;

  const principals = [
    ...new Set(
      restoreAccounts.map(
        (account) =>
          `arn:aws:iam::${account.providerAccountId}:role/EonRestoreNodeRole`
      )
    ),
  ];

  const principalValue =
    principals.length === 0
      ? '"<restore-account-role-arn>"'
      : principals.length === 1
        ? `"${principals[0]}"`
        : `[${principals.map((arn) => `"${arn}"`).join(', ')}]`;

  return `{
    "Version": "2012-10-17",
    "Statement": [
        {
            "Sid": "AllowGetObjectFromRestoreAccount",
            "Effect": "Allow",
            "Principal": {
                "AWS": ${principalValue}
            },
            "Action": [
                "s3:GetObject"
            ],
            "Resource": [
                "${mrapArn}/object/*"
            ]
        }
    ]
}`;
};
